<template>
  <div>
    <h2>SIGN INTO YOUR ACCOUNT</h2>
    <p>
      Fill in your details below to access Members Only benefits and shop the
      sale first.
    </p>

    <SingleSignOn theme="primary-light" :return-url="returnUrl" />

    <div class="divider">or</div>

    <div v-if="hasErrors" class="error-messages">
      <ul>
        <li v-for="error in errors" :key="error.code">
          {{ error.message }}
        </li>
      </ul>
    </div>

    <form class="form" @submit.prevent="handleFormSubmit">
      <TextField
        theme="white"
        :value="email"
        :validation="v$.email"
        autocomplete="email"
        type="email"
        placeholder="E-mail address"
        @input="(value) => (email = value)"
      />

      <TextField
        theme="white"
        :value="password"
        :validation="v$.password"
        autocomplete="password"
        type="password"
        placeholder="Password"
        @input="(value) => (password = value)"
      />

      <div class="field">
        <Button
          type="submit"
          width="full"
          :loading="loading"
          theme="primary-light"
        >
          Sign in
        </Button>
      </div>
    </form>

    <Button
      v-if="!hideRegisterLink"
      width="full"
      theme="outline-light"
      class="register-button"
      @click="$emit('register')"
    >
      Need to create an account?
    </Button>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required, email, helpers } from "@vuelidate/validators";
import SingleSignOn from "@/components/SingleSignOn.vue";
import TextField from "@/components/TextField.vue";
import Button from "@/components/ButtonV3.vue";
import isEmpty from "lodash-es/isEmpty";
import { mapActions } from "vuex";

export default {
  name: "LandingLoginForm",
  components: {
    SingleSignOn,
    TextField,
    Button,
  },
  props: {
    returnUrl: {
      type: String,
      default: null,
    },
    hideRegisterLink: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["register", "success"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      email: "",
      password: "",
      loading: false,
      errors: [],
    };
  },
  validations() {
    return {
      email: {
        required: helpers.withMessage("Email address is required", required),
        email,
      },
      password: {
        required: helpers.withMessage("Password is required", required),
      },
    };
  },
  computed: {
    hasErrors() {
      return this.errors.length > 0;
    },
  },
  methods: {
    ...mapActions("auth", ["attemptLogin"]),
    async handleFormSubmit() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      const loginObj = {
        email: this.email,
        password: this.password,
      };

      this.loading = true;
      const { customerAccessToken, customerUserErrors } =
        await this.attemptLogin(loginObj);

      if (customerUserErrors) {
        this.errors = customerUserErrors;
        this.loading = false;
      }

      if (!isEmpty(customerAccessToken)) {
        this.$emit("success");
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.button-group {
  display: flex;
  gap: 20px;
  flex-direction: column;
}

h2 {
  @include editorial-headline(24px);
  text-transform: uppercase;
  margin-bottom: 16px;
  color: $color-white;
}

p {
  @include editorial-body(14px);
  margin-bottom: 28px;
  color: $color-white;
}

.divider {
  position: relative;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 28px;
  @include editorial-body(14px);
  color: $color-white;

  @include respond-above(md) {
    color: $color-white;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border-bottom: solid 1px rgba($color-white, 0.3);
    width: 40%;
    left: 0;

    @include respond-above(md) {
      border-bottom: solid 1px rgba($color-white, 0.3);
    }
  }

  &::after {
    left: auto;
    right: 0;
  }
}

.sso-buttons {
  border-bottom: none;
  margin: 0 0 28px;
  padding: 0;

  :deep(.button) {
    color: $color-black;
    border-color: $color-white;
    background-color: $color-white;
  }
}

.countdown {
  margin-bottom: 28px;
}

.footnote {
  margin-top: 28px;
  @include utility-label(13px);
  text-align: center;
  color: $color-white;

  @include respond-above(md) {
    color: $color-black;
  }
}

.error-messages {
  margin-top: 10px;
  margin-bottom: 10px;
  background-color: $color-red;
  padding: 16px;
  color: $color-white;
  border-radius: 2px;
  font-size: 10px;
}

:deep(.field) {
  .input {
    border-color: rgba($color-white, 0.5);
    color: $color-white;

    &__element {
      color: $color-white;
      &::placeholder {
        color: rgba($color-white, 0.9);
      }
    }
  }
}

:deep(.field--white) {
  .input__element {
    &::placeholder {
      color: rgba($color-white, 0.9);
    }
  }
}

:deep(.button--primary-light) {
  background-color: $color-white;
  color: $color-black;
  border-color: $color-white;

  @include respond-above(md) {
    background-color: $color-white;
    color: $color-black;
    border-color: $color-white;
  }
}

:deep(.button--outline-light) {
  border-color: $color-white;
  color: $color-white;
}

.register-button {
  margin-top: 20px;
}
</style>
